import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import axios from "axios";

import {
    Button,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Select,
    Table,
    Upload
} from "antd";

import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons";

import Appconfig from "../../../config/config";
import "./WelcomeBanner.css";

function WelcomeBanner() {
    const userInfo = useMemo(() => {
        try {
            return JSON.parse(
                localStorage.getItem("userdata")
            );
        } catch (error) {
            return null;
        }
    }, []);

    /*
     * Logged-in admin ka site_code priority hai.
     * Appconfig.sitecodes fallback hai.
     */
    const siteCode = String(
        userInfo?.site_code ||
        Appconfig.sitecodes ||
        ""
    ).trim();

    const apiBaseUrl = String(
        Appconfig.apiUrl || ""
    ).replace(/\/+$/, "");

    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [deletingId, setDeletingId] =
        useState("");

    const [modalOpen, setModalOpen] =
        useState(false);

    const [editingBanner, setEditingBanner] =
        useState(null);

    const [selectedFile, setSelectedFile] =
        useState(null);

    const [formData, setFormData] = useState({
        title:
            "Beware Of Phishing Websites Before Login. Enable Security Auth To Secure Your ID.",
        display_order: 0,
        display_duration: 5,
        status: "Active"
    });

    const getImageUrl = useCallback(
        (imagePath) => {
            if (!imagePath) {
                return "";
            }

            if (
                imagePath.startsWith("http://") ||
                imagePath.startsWith("https://")
            ) {
                return imagePath;
            }

            return `${apiBaseUrl}${imagePath.startsWith("/") ? "" : "/"
                }${imagePath}`;
        },
        [apiBaseUrl]
    );

    const resetForm = useCallback(() => {
        setEditingBanner(null);
        setSelectedFile(null);

        setFormData({
            title:
                "Beware Of Phishing Websites Before Login. Enable Security Auth To Secure Your ID.",
            display_order: 0,
            display_duration: 5,
            status: "Active"
        });
    }, []);

    const openAddModal = () => {
        resetForm();
        setModalOpen(true);
    };

    const openEditModal = (banner) => {
        setEditingBanner(banner);
        setSelectedFile(null);

        setFormData({
            title: banner?.title || "",
            display_order:
                Number(banner?.display_order) || 0,
            display_duration:
                Number(banner?.display_duration) || 5,
            status:
                banner?.status === "Inactive"
                    ? "Inactive"
                    : "Active"
        });

        setModalOpen(true);
    };

    const closeModal = () => {
        if (saving) {
            return;
        }

        setModalOpen(false);
        resetForm();
    };

    const fetchBanners = useCallback(
        async (showLoader = true) => {
            if (!siteCode) {
                setBanners([]);

                message.error(
                    "Admin configuration is missing"
                );

                return;
            }

            if (showLoader) {
                setLoading(true);
            }

            try {
                const response = await axios.post(
                    `${apiBaseUrl}/welcomeBanner/list`,
                    {
                        site_code: siteCode
                    },
                    {
                        headers: {
                            "Content-Type":
                                "application/json"
                        }
                    }
                );

                if (response.data?.result === 1) {
                    setBanners(
                        Array.isArray(
                            response.data.resultData
                        )
                            ? response.data.resultData
                            : []
                    );
                } else {
                    setBanners([]);

                    message.error(
                        response.data?.resultMessage ||
                        "Unable to fetch welcome banners"
                    );
                }
            } catch (error) {
                console.error(
                    "WELCOME BANNER LIST ERROR:",
                    error
                );

                setBanners([]);

                message.error(
                    error.response?.data?.resultMessage ||
                    "Unable to fetch welcome banners"
                );
            } finally {
                if (showLoader) {
                    setLoading(false);
                }
            }
        },
        [apiBaseUrl, siteCode]
    );

    useEffect(() => {
        fetchBanners();
    }, [fetchBanners]);

    const handleSave = async () => {
        if (!siteCode) {
            message.error(
                "Logged-in admin site code is missing"
            );
            return;
        }

        if (!formData.title.trim()) {
            message.error(
                "Welcome banner message is required"
            );
            return;
        }

        if (!editingBanner && !selectedFile) {
            message.error(
                "Please select a banner image"
            );
            return;
        }

        setSaving(true);

        try {
            let response;

            /*
             * Edit mode mein image change nahi hogi.
             * Title, order, duration aur status update honge.
             */
            if (editingBanner) {
                response = await axios.post(
                    `${apiBaseUrl}/welcomeBanner/update`,
                    {
                        id: editingBanner._id,
                        title: formData.title.trim(),
                        display_order:
                            Number(
                                formData.display_order
                            ) || 0,
                        display_duration: Math.max(
                            1,
                            Number(
                                formData.display_duration
                            ) || 5
                        ),
                        status: formData.status
                    },
                    {
                        headers: {
                            "Content-Type":
                                "application/json"
                        }
                    }
                );
            } else {
                const uploadData = new FormData();

                uploadData.append(
                    "site_code",
                    siteCode
                );

                uploadData.append(
                    "title",
                    formData.title.trim()
                );

                uploadData.append(
                    "display_order",
                    String(
                        Number(
                            formData.display_order
                        ) || 0
                    )
                );

                uploadData.append(
                    "display_duration",
                    String(
                        Math.max(
                            1,
                            Number(
                                formData.display_duration
                            ) || 5
                        )
                    )
                );

                uploadData.append(
                    "status",
                    formData.status
                );

                uploadData.append(
                    "file",
                    selectedFile
                );

                /*
                 * Multipart Content-Type manually mat lagana.
                 * Browser boundary automatically add karega.
                 */
                response = await axios.post(
                    `${apiBaseUrl}/welcomeBanner/add`,
                    uploadData
                );
            }

            if (response.data?.result !== 1) {
                throw new Error(
                    response.data?.resultMessage ||
                    "Unable to save welcome banner"
                );
            }

            message.success(
                response.data?.resultMessage ||
                "Welcome banner saved successfully"
            );

            setModalOpen(false);
            resetForm();

            await fetchBanners(false);
        } catch (error) {
            console.error(
                "SAVE WELCOME BANNER ERROR:",
                error
            );

            message.error(
                error.response?.data?.resultMessage ||
                error.message ||
                "Unable to save welcome banner"
            );
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (
        banner,
        newStatus
    ) => {
        try {
            const response = await axios.post(
                `${apiBaseUrl}/welcomeBanner/update`,
                {
                    id: banner._id,
                    status: newStatus
                },
                {
                    headers: {
                        "Content-Type":
                            "application/json"
                    }
                }
            );

            if (response.data?.result !== 1) {
                throw new Error(
                    response.data?.resultMessage ||
                    "Unable to update banner status"
                );
            }

            message.success(
                `Banner ${newStatus.toLowerCase()} successfully`
            );

            await fetchBanners(false);
        } catch (error) {
            console.error(
                "WELCOME BANNER STATUS ERROR:",
                error
            );

            message.error(
                error.response?.data?.resultMessage ||
                error.message ||
                "Unable to update banner status"
            );
        }
    };

    const handleDelete = async (bannerId) => {
        setDeletingId(bannerId);

        try {
            const response = await axios.post(
                `${apiBaseUrl}/welcomeBanner/delete`,
                {
                    id: bannerId
                },
                {
                    headers: {
                        "Content-Type":
                            "application/json"
                    }
                }
            );

            if (response.data?.result !== 1) {
                throw new Error(
                    response.data?.resultMessage ||
                    "Unable to delete welcome banner"
                );
            }

            message.success(
                response.data?.resultMessage ||
                "Welcome banner deleted successfully"
            );

            await fetchBanners(false);
        } catch (error) {
            console.error(
                "DELETE WELCOME BANNER ERROR:",
                error
            );

            message.error(
                error.response?.data?.resultMessage ||
                error.message ||
                "Unable to delete welcome banner"
            );
        } finally {
            setDeletingId("");
        }
    };

    const columns = [
        {
            title: "Preview",
            key: "preview",
            width: 150,
            render: (_, banner) => (
                <img
                    className="welcome-banner-preview"
                    src={getImageUrl(
                        banner?.image_url
                    )}
                    alt={
                        banner?.title ||
                        "Welcome banner"
                    }
                />
            )
        },
        {
            title: "Message",
            dataIndex: "title",
            key: "title",
            render: (title) => (
                <span className="welcome-table-title">
                    {title || "No message"}
                </span>
            )
        },
        // {
        //     title: "Site Code",
        //     dataIndex: "site_code",
        //     key: "site_code",
        //     width: 110
        // },
        {
            title: "Order",
            dataIndex: "display_order",
            key: "display_order",
            width: 85
        },
        {
            title: "Duration",
            dataIndex: "display_duration",
            key: "display_duration",
            width: 105,
            render: (duration) =>
                `${Number(duration) || 5} sec`
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 130,
            render: (status, banner) => (
                <Select
                    value={
                        status === "Inactive"
                            ? "Inactive"
                            : "Active"
                    }
                    className="welcome-status-select"
                    onChange={(newStatus) =>
                        handleStatusChange(
                            banner,
                            newStatus
                        )
                    }
                    options={[
                        {
                            value: "Active",
                            label: "Active"
                        },
                        {
                            value: "Inactive",
                            label: "Inactive"
                        }
                    ]}
                />
            )
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_, banner) => (
                <div className="welcome-banner-actions">
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        title="Edit banner"
                        onClick={() =>
                            openEditModal(banner)
                        }
                    />

                    <Popconfirm
                        title="Delete welcome banner?"
                        description="The uploaded image will also be removed."
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{
                            danger: true
                        }}
                        onConfirm={() =>
                            handleDelete(banner._id)
                        }
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            title="Delete banner"
                            loading={
                                deletingId === banner._id
                            }
                        />
                    </Popconfirm>
                </div>
            )
        }
    ];

    return (
        <main className="welcome-banner-page">
            <section className="welcome-banner-card">
                <div className="welcome-banner-heading">
                    <div className="welcome-heading-content">
                        <span className="welcome-heading-icon">
                            🖼
                        </span>

                        <div>
                            <h1>
                                Welcome Banner Management
                            </h1>

                            <p>
                                Manage login welcome banners
                                {/* for site:{" "}
                                <strong>
                                    {siteCode || "N/A"}
                                </strong> */}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="welcome-add-button"
                        onClick={openAddModal}
                    >
                        <PlusOutlined />

                        <span>Add Banner</span>
                    </button>
                </div>

                <div className="welcome-banner-summary">
                    <div>
                        <span>Total Banners</span>

                        <strong>
                            {banners.length}
                        </strong>
                    </div>

                    <div>
                        <span>Active</span>

                        <strong>
                            {
                                banners.filter(
                                    (banner) =>
                                        banner.status ===
                                        "Active"
                                ).length
                            }
                        </strong>
                    </div>

                    <div>
                        <span>Inactive</span>

                        <strong>
                            {
                                banners.filter(
                                    (banner) =>
                                        banner.status !==
                                        "Active"
                                ).length
                            }
                        </strong>
                    </div>
                </div>

                <div className="welcome-banner-table-wrapper">
                    <Table
                        rowKey={(banner) =>
                            banner._id
                        }
                        columns={columns}
                        dataSource={banners}
                        loading={loading}
                        pagination={false}
                        className="welcome-banner-table"
                        locale={{
                            emptyText:
                                "No welcome banners found for this site."
                        }}
                        scroll={{
                            x: 1000
                        }}
                    />
                </div>
            </section>

            <Modal
                open={modalOpen}
                onCancel={closeModal}
                footer={null}
                closable={false}
                destroyOnClose
                width={650}
                className="welcome-banner-modal"
            >
                <div className="welcome-modal-header">
                    <div>
                        <h2>
                            {editingBanner
                                ? "Edit Welcome Banner"
                                : "Add Welcome Banner"}
                        </h2>

                        {/* <p>
                            Site code: {siteCode || "N/A"}
                        </p> */}
                    </div>

                    <button
                        type="button"
                        className="welcome-modal-close"
                        onClick={closeModal}
                        disabled={saving}
                    >
                        ×
                    </button>
                </div>

                <div className="welcome-modal-body">
                    {!editingBanner ? (
                        <div className="welcome-form-field">
                            <label>Banner Image</label>

                            <Upload
                                accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.bmp,.ico"
                                maxCount={1}
                                beforeUpload={(file) => {
                                    setSelectedFile(file);
                                    return false;
                                }}
                                onRemove={() => {
                                    setSelectedFile(null);
                                }}
                                fileList={
                                    selectedFile
                                        ? [selectedFile]
                                        : []
                                }
                            >
                                <Button
                                    icon={<UploadOutlined />}
                                >
                                    Select Image
                                </Button>
                            </Upload>

                            <small>
                                Maximum size 10 MB.
                            </small>
                        </div>
                    ) : (
                        <div className="welcome-current-image">
                            <label>Current Image</label>

                            <img
                                src={getImageUrl(
                                    editingBanner.image_url
                                )}
                                alt={
                                    editingBanner.title ||
                                    "Welcome banner"
                                }
                            />
                        </div>
                    )}

                    <div className="welcome-form-field">
                        <label>Header Message</label>

                        <Input.TextArea
                            rows={3}
                            value={formData.title}
                            placeholder="Enter welcome message"
                            onChange={(event) =>
                                setFormData((previous) => ({
                                    ...previous,
                                    title: event.target.value
                                }))
                            }
                        />
                    </div>

                    <div className="welcome-form-grid">
                        <div className="welcome-form-field">
                            <label>Display Order</label>

                            <InputNumber
                                min={0}
                                value={formData.display_order}
                                onChange={(value) =>
                                    setFormData((previous) => ({
                                        ...previous,
                                        display_order:
                                            Number(value) || 0
                                    }))
                                }
                            />
                        </div>

                        <div className="welcome-form-field">
                            <label>
                                Change After (seconds)
                            </label>

                            <InputNumber
                                min={1}
                                max={300}
                                value={
                                    formData.display_duration
                                }
                                onChange={(value) =>
                                    setFormData((previous) => ({
                                        ...previous,
                                        display_duration:
                                            Math.max(
                                                1,
                                                Number(value) || 5
                                            )
                                    }))
                                }
                            />
                        </div>
                    </div>

                    <div className="welcome-form-field">
                        <label>Status</label>

                        <Select
                            value={formData.status}
                            onChange={(value) =>
                                setFormData((previous) => ({
                                    ...previous,
                                    status: value
                                }))
                            }
                            options={[
                                {
                                    value: "Active",
                                    label: "Active"
                                },
                                {
                                    value: "Inactive",
                                    label: "Inactive"
                                }
                            ]}
                        />
                    </div>
                </div>

                <div className="welcome-modal-footer">
                    <Button
                        onClick={closeModal}
                        disabled={saving}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="primary"
                        loading={saving}
                        onClick={handleSave}
                    >
                        {editingBanner
                            ? "Update Banner"
                            : "Add Banner"}
                    </Button>
                </div>
            </Modal>
        </main>
    );
}

export default WelcomeBanner;